import { DeliveryProvider } from '../../services/exports/Constants';
import { Button, Form, InputNumber } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import collect from 'collect.js';

export default function OrderPreparationTimeOptions({
  order,
  preparationTime,
  setPreparationTime,
  setSelectedTimeValid,
}) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderPreparationTimeOptions' });

  const { company } = useSelector((state) => state.company);
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const isExternalDelivery = OrderHelpers.isExternalDelivery(order);
  const timeStep = company?.order_approval_time_step ?? 10;

  const minPreparationTime = useMemo(() => {
    if (!isExternalDelivery) {
      return null;
    }

    const fromCompany = company?.default_delivery_prep_times?.[order.delivery_provider];

    if (fromCompany) {
      return fromCompany;
    }

    if (order.delivery_provider === DeliveryProvider.WoltDrive) {
      return 15;
    }

    if (order.delivery_provider === DeliveryProvider.UberDrive) {
      return 20;
    }

    return 30;
  }, [isExternalDelivery, timeStep, company?.default_delivery_prep_times, order.delivery_provider]);

  const maxPreparationTime = useMemo(() => {
    if (isExternalDelivery && order.delivery_provider === DeliveryProvider.WoltDrive) {
      return 60;
    }

    return timeStep * 9 < 60 ? 60 : timeStep * 8;
  }, [isExternalDelivery, timeStep]);

  const selectedTimeValid =
    !useCustomTime ||
    ((!minPreparationTime || minPreparationTime <= selectedTime) && maxPreparationTime >= selectedTime);

  useEffect(() => {
    selectedTime && setPreparationTime(+selectedTime);
  }, [selectedTime]);

  useEffect(() => {
    setSelectedTimeValid(selectedTimeValid);
  }, [selectedTimeValid]);

  const timeOptions = useMemo(
    () =>
      collect()
        .times(
          6,
          (i) =>
            (minPreparationTime ?? order.estimated_preparation_time) +
            company.busy_mode_minutes -
            timeStep +
            timeStep * i,
        )
        .filter((item) => item <= maxPreparationTime)
        .toArray(),
    [
      minPreparationTime,
      order.estimated_preparation_time,
      company.busy_mode_minutes,
      preparationTime,
      timeStep,
      maxPreparationTime,
    ],
  );

  const onSelectTime = (option) => {
    setSelectedTime(option);
    setUseCustomTime(false);
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      {timeOptions.map((item, index) => (
        <Button
          color={index < timeOptions.length - 2 || (!useCustomTime && item === selectedTime) ? 'primary' : 'danger'}
          variant={
            !useCustomTime && item === selectedTime ? 'solid' : index >= timeOptions.length - 2 ? 'filled' : 'outlined'
          }
          onClick={() => onSelectTime(item)}
          className="h-16"
          key={`time-option-${index}`}
        >
          {`${item} min`}
        </Button>
      ))}
      <div className="col-span-3">
        {useCustomTime ? (
          <div className="space-y-sm">
            <div className="bg-red-50 border border-red-200 p-sm rounded-lg">
              <Form.Item className="mb-0" layout="vertical" label={t(`form.preparation_time.label`)}>
                <InputNumber
                  value={selectedTime}
                  placeholder={t(`form.preparation_time.placeholder`)}
                  onChange={(value) => setSelectedTime(value)}
                  className="w-full"
                />
              </Form.Item>
            </div>
            {useCustomTime && (!selectedTime || selectedTime === 0) && (
              <p className="text-danger">{t(`feedback.preparation_time_required`)}</p>
            )}
            {minPreparationTime && minPreparationTime > selectedTime && (
              <p className="text-danger">
                {t(`feedback.min_preparation_time`, {
                  minutes: minPreparationTime,
                })}
              </p>
            )}
            {selectedTime > maxPreparationTime && (
              <p className="text-danger">
                {t('feedback.max_preparation_time', {
                  minutes: maxPreparationTime,
                })}
              </p>
            )}
          </div>
        ) : (
          <Button className="col-span-3 w-full" onClick={() => setUseCustomTime(true)} size="large">
            {t(`buttons.custom`)}
          </Button>
        )}
      </div>
    </div>
  );
}
